import type {
  ComicDraft,
  ListingCategory,
  ListingFormat,
  ListingType,
  ListingVisibility,
  PreorderStatus,
  RawImageRef,
  UnlistedReason,
} from "@kraahl/contract"

import type { PhotoEditorPersistedState } from "@/lib/editor-types"

// Sale-model + production-mode + preorder-status discriminators — canonical
// declarations live in `packages/contract/src/listing-adt.ts`. Re-exported
// here so every existing `@/types/listing` import site (web + tests) keeps
// working without ripping each importer over to the package path in one go.
// Phase 2 of `shippo-listing-discriminator`.
export type { ListingFormat, ListingType, PreorderStatus }

// Category discriminator — canonical tuple lives in
// `packages/contract/src/listing-category.ts` (Wave 0, NEW_CATEGORIES_PLAN §3.1).
export type { ListingCategory }
// Typed comic draft — canonical schema in `packages/contract/src/listing-comic.ts`.
export type { ComicDraft }

/**
 * Card-product packaging format — orthogonal to sale model (`ListingFormat`).
 * "single" / "slab" / "bundle" / etc. describe *what* the product is, not
 * how it is sold. Renamed from `CardListingFormat` in Phase 2 to remove the
 * shared `ListingFormat` substring that suggested a sale-model link.
 */
export type CardPackagingFormat =
  | "single"
  | "slab"
  | "bundle"
  | "booster"
  | "box"
  | "etb"
  | "deck"
  | "collection"

export type CardRawCondition = "M" | "NM" | "LP" | "MP" | "HP" | "DMG"

export interface CardSpecialAttributes {
  isFirstEdition: boolean
  isShadowless: boolean
  isReverseHolo: boolean
  isStampedPromo: boolean
  isMisprint: boolean
  isAltArt: boolean
  isSigned: boolean
  isAutograph: boolean
  isPatchRelic: boolean
  isRookie: boolean
  isFoilVariant: boolean
  isForeignBlackBorder: boolean
}

export interface CardGradingDetails {
  companyId: string
  gradeId?: string
  certNumber?: string
  bgsSubgrades?: {
    centering: number
    corners: number
    edges: number
    surface: number
  }
}

export interface CardCentering {
  leftPct: number
  topPct: number
}

export type CardSurfaceDefects = Array<
  | "scratches"
  | "holo_damage"
  | "print_line"
  | "silvering"
  | "crease"
  | "whitening"
  | "stain"
  | "indentation"
>

export type MtgPrintVariant =
  | "alpha"
  | "beta"
  | "unlimited"
  | "revised"
  | "fbb"
  | "fwb"
  | "summer"
  | "ce"
  | "ice_age"

export const EMPTY_SPECIAL_ATTRIBUTES: CardSpecialAttributes = {
  isFirstEdition: false,
  isShadowless: false,
  isReverseHolo: false,
  isStampedPromo: false,
  isMisprint: false,
  isAltArt: false,
  isSigned: false,
  isAutograph: false,
  isPatchRelic: false,
  isRookie: false,
  isFoilVariant: false,
  isForeignBlackBorder: false,
}

export interface CardListingDraft {
  cardCategory: "trading_card"
  format: CardPackagingFormat
  tcgId?: string
  tcgSetId?: string
  tcgRarityId?: string
  languageId?: string
  conditionId?: string
  cardName: string
  cardNumber?: string
  printYear?: number
  printRunNumber?: number
  printRunTotal?: number
  specialAttributes: CardSpecialAttributes
  mtgPrintVariant?: MtgPrintVariant
  sport?: string
  playerName?: string
  rookieSeason?: string
  lotCardCount?: number
  lotCommonsCount?: number
  lotUncommonsCount?: number
  lotRaresPlusCount?: number
  provenanceTags?: string[]
  provenanceNotes?: string
  certVerifyUrl?: string
  previousSaleUrl?: string
  grading?: CardGradingDetails
  centering?: CardCentering
  surfaceDefects?: CardSurfaceDefects
  isGraded: boolean
  marketReferencePrice?: number
  marketReferenceSource?: string
}

export interface ListingRow {
  id: string
  created_at: string
  title: string
  description: string
  price: number | null
  currency?: string | null
  shipping_config: string
  /**
   * @deprecated ADR_LISTING_SHIP_FROM_ADDRESS — the underlying
   * `listings.location` column is dropped in Phase 4 migration
   * `20260622220200_drop_listings_location_column.sql`. Reads return NULL
   * after the migration applies. Several Phase 1-3 callsites (edit-listing
   * dialog form state, admin dashboard, search/feed user cards, chat
   * listing selector) still read this field; clean up in follow-up.
   */
  location?: string
  grade: string | number
  condition?: string
  quantity?: number
  /**
   * RawImageRef: NOT threaded — `ListingRow` is the wide DB-row shape read at
   * ~400 sites across app/, components/, lib/ and hooks/; branding here would
   * force a repo-wide sweep far past the value it buys. The brand is applied
   * where it is load-bearing instead: the WRITE bodies
   * (`@kraahl/contract` `image_urls` on listing-create / listing-patch) and the
   * edit-flow `PhotoItem.preview` chain, i.e. every path that can push a
   * resolved absolute URL back into the column and undo phase C.
   *
   * Read sites still owe a `resolveStorageUrl` before any absolute-URL sink;
   * most get it for free from `toSupabasePublicImageThumbUrl` /
   * `parseListingImageUrls`.
   *
   * @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §11
   */
  image_urls?: string[]
  /**
   * Phase 4 (2026-06-29) of `shippo-listing-discriminator`: orthogonal
   * visibility axis. Replaces the dropped `is_chat_private` boolean
   * column. Pre-Phase-4 row snapshots may carry `null`/undefined → treat
   * as `public`.
   */
  visibility?: ListingVisibility | null
  chat_private_saved?: boolean | null
  is_unlisted?: boolean
  /**
   * Why `is_unlisted` is true. NULL = seller self-hide;
   * `admin_tos_violation` = operator suspension (seller cannot lift it —
   * see `lib/admin/listing-suspension.ts` / DB trigger
   * `listings_guard_admin_unlist`).
   */
  unlisted_reason?: UnlistedReason | null
  /** Audit trail of the current admin suspension — operator-only columns. */
  suspended_at?: string | null
  suspended_by?: string | null
  scheduled_publish_at?: string | null
  cardCategory?: ListingCategory | null
  cardDraft?: CardListingDraft | null
  // Category fields
  brand_id?: string | null
  theme_id?: string | null
  product_type_id?: string | null
  theme_custom_text?: string | null
  brand_custom_text?: string | null
  product_type_custom_text?: string | null
  // Auction fields
  listing_format?: string | null
  // Phase 3 (2026-06-29): `accepts_offers` collapsed into `listing_format`.
  // Use `isOfferEnabledFormat()` from `@kraahl/contract` to derive.
  auto_decline_below?: number | null
  auction_start_price?: number | null
  auction_highest_bid?: number | null
  auction_end_at?: string | null
  auction_status?: string | null
  auction_bid_count?: number | null
  // Preorder fields
  type?: ListingType | null
  preorder_deadline?: string | null
  estimated_delivery?: string | null
  min_orders?: number | null
  max_orders?: number | null
  preorder_status?: PreorderStatus | null
  // Enriched fields (added client-side, not from DB)
  total_orders?: number
  total_saves?: number
  collections?: string[]
  // Shipping parsed
  shipping?: {
    methods: string[]
    customOptions: any[]
  }
  user_id?: string
}

/**
 * Structural twin of the `PhotoItem` declared in
 * `components/my-listings/{edit-listing-dialog,simple-photo-editor-production}.tsx`.
 * `preview` is BOTH display and persistence, so it stays RAW and is branded —
 * see those declarations for the full rationale (handoff §9).
 */
export interface PhotoItem {
  file: File | null
  preview: RawImageRef
  photoId: string // Required for order persistence
}

export interface ShippingOption {
  id: string
  label: string
  price?: number
  allowCombined?: boolean
  combinedFee?: number
}

export interface ShippingData {
  methods: string[]
  customOptions: ShippingOption[]
}

/**
 * Shippo white-label (WS3) — listing-level shipping mode + parcel dims.
 *  - `'legacy'` (default): flat-rate seller methods via shipping.methods.
 *  - `'live_rate'`: carrier-provider live rates; requires parcel + provider.
 *
 * Mirrors `listings.shipping_mode` + `listings.carrier_provider` +
 * `listings.parcel_*` in `20260617120000_provider_neutral_shipping.sql`.
 * Parcel sub-shape is decoupled from `lib/shipping/types.ts` (which is a
 * pure domain ADT — no draft semantics).
 */
export type ShippingMode = "legacy" | "live_rate"

/**
 * Slice C2 (2026-06-23): canonical-only — mass in grams, distances in
 * millimetres. UI display ⇄ canonical conversion is encapsulated in
 * `ShippingModeToggleAndParcelForm`; persistence in `edit-listing-state.ts`
 * reads the DB-row canonical columns directly.
 */
export interface ParcelDraft {
  weight?: number
  length?: number
  width?: number
  height?: number
}

// `ListingFormat` / `ListingType` / `PreorderStatus` are re-exported from
// `@kraahl/contract` at the top of this file (Phase 2 single-SoT lift) —
// the canonical tuples live in `packages/contract/src/listing-adt.ts`.

export interface PreorderFields {
  preorder_deadline?: string | null
  estimated_delivery?: string | null
  min_orders?: number | null
  max_orders?: number | null
}

export interface ListingData {
  id?: string
  title: string
  description: string
  price?: number
  currency?: string
  photos: PhotoItem[]
  editedPhotos?: PhotoItem[]
  photoEditorState?: PhotoEditorPersistedState
  condition: string
  grade: string
  gradeLabel?: string
  shipping: ShippingData
  quantity: number
  brand_id?: string
  theme_id?: string
  product_type_id?: string
  theme_custom_text?: string
  brand_custom_text?: string
  product_type_custom_text?: string
  listing_format?: ListingFormat
  // Phase 3 (2026-06-29): legacy `accepts_offers` collapsed into
  // `listing_format`. Derive via `isOfferEnabledFormat()` from
  // `@kraahl/contract`.
  auto_decline_below?: number | null
  auction_start_price?: number
  auction_reserve_price?: number | null
  auction_end_at?: string | null
  listing_type?: ListingType
  preorder_deadline?: string | null
  estimated_delivery?: string | null
  min_orders?: number | null
  max_orders?: number | null
  scheduled_publish_at?: string | null
  is_dev_listing?: boolean
  /**
   * Phase 4 (2026-06-29) of `shippo-listing-discriminator`: orthogonal
   * visibility axis (replaces the dropped `is_chat_private` boolean).
   */
  visibility?: ListingVisibility
  chat_private_saved?: boolean
  createdAt?: Date
  updatedAt?: Date
  listingCategory?: ListingCategory
  cardDraft?: CardListingDraft
  /** Typed comic draft — canonical schema in `@kraahl/contract` (listing-comic.ts). */
  comicDraft?: ComicDraft
  shippingMode?: ShippingMode
  parcel?: ParcelDraft
  /**
   * Per-listing ship-from override picker — `address_book.id` chosen by
   * the seller in the ShippingStep. Undefined ≡ "use my main address"
   * (the row stays NULL server-side, resolution falls back to `is_main`).
   * See ADR_LISTING_SHIP_FROM_ADDRESS.md.
   */
  ship_from_address_id?: string
}

export interface StepProps {
  data: ListingData
  updateDataAction: (updates: Partial<ListingData>) => void
  onNextAction: () => void
  onPrevAction: () => void
  onGoToStepAction?: (step: number) => void
  isLastStep?: boolean
  isStepValid?: boolean
  onSubmit?: (data?: ListingData) => void
  onValidationChange?: (isValid: boolean) => void
  hideButton?: boolean
  onRegisterSave?: (saveFunction: (() => void | Promise<void>) | null) => void
  compact?: boolean
  isMobile?: boolean
  allowAuctionOption?: boolean
  fullViewport?: boolean
}
